import { ChainId } from '@aave/contract-helpers';
import {
  AaveV2Avalanche as V2Avalanche,
  AaveV2Ethereum as V2Ethereum,
  AaveV2Fuji as V2Fuji,
  AaveV2Polygon as V2Polygon,
  AaveV3Arbitrum as V3Arbitrum,
  AaveV3Avalanche as V3Avalanche,
  AaveV3Base as V3Base,
  AaveV3BaseSepolia as V3BaseSepolia,
  AaveV3BNB as V3BNB,
  AaveV3Celo as V3Celo,
  AaveV3Ethereum as V3Ethereum,
  AaveV3EthereumEtherFi as V3EthereumEtherFi,
  AaveV3EthereumHorizon as V3EthereumHorizon,
  AaveV3EthereumLido as V3EthereumLido,
  AaveV3Gnosis as V3Gnosis,
  AaveV3InkWhitelabel as V3InkWhitelabel,
  AaveV3Linea as V3Linea,
  AaveV3Mantle as V3Mantle,
  AaveV3MegaEth as V3MegaEth,
  AaveV3Metis as V3Metis,
  AaveV3Optimism as V3Optimism,
  AaveV3Plasma as V3Plasma,
  AaveV3Polygon as V3Polygon,
  AaveV3Scroll as V3Scroll,
  AaveV3Soneium as V3Soneium,
  AaveV3Sonic as V3Sonic,
  AaveV3XLayer as V3XLayer,
  AaveV3ZkSync as V3ZkSync,
  GhoAvalanche,
  GhoGnosis,
  GhoMantle,
  GhoPlasma,
} from '@aave-dao/aave-address-book';
import { ReactNode } from 'react';

const zeroAddress = '0x0000000000000000000000000000000000000000';
const addressPattern = /^0x[a-fA-F0-9]{40}$/;
const dinolendEnv: Record<string, string | undefined> = {
  NEXT_PUBLIC_DINOLEND_POOL_ADDRESSES_PROVIDER:
    process.env.NEXT_PUBLIC_DINOLEND_POOL_ADDRESSES_PROVIDER,
  NEXT_PUBLIC_DINOLEND_POOL: process.env.NEXT_PUBLIC_DINOLEND_POOL,
  NEXT_PUBLIC_DINOLEND_POOL_CONFIGURATOR: process.env.NEXT_PUBLIC_DINOLEND_POOL_CONFIGURATOR,
  NEXT_PUBLIC_DINOLEND_PRICE_ORACLE: process.env.NEXT_PUBLIC_DINOLEND_PRICE_ORACLE,
  NEXT_PUBLIC_DINOLEND_WETH_GATEWAY: process.env.NEXT_PUBLIC_DINOLEND_WETH_GATEWAY,
  NEXT_PUBLIC_DINOLEND_WALLET_BALANCE_PROVIDER:
    process.env.NEXT_PUBLIC_DINOLEND_WALLET_BALANCE_PROVIDER,
  NEXT_PUBLIC_DINOLEND_UI_POOL_DATA_PROVIDER:
    process.env.NEXT_PUBLIC_DINOLEND_UI_POOL_DATA_PROVIDER,
  NEXT_PUBLIC_DINOLEND_UI_INCENTIVE_DATA_PROVIDER:
    process.env.NEXT_PUBLIC_DINOLEND_UI_INCENTIVE_DATA_PROVIDER,
  NEXT_PUBLIC_DINOLEND_COLLECTOR: process.env.NEXT_PUBLIC_DINOLEND_COLLECTOR,
};
const readEnvAddress = (name: string, fallback = zeroAddress) =>
  addressPattern.test(dinolendEnv[name] || '') ? dinolendEnv[name] || fallback : fallback;
const dinolendMarketChainId = Number(
  process.env.NEXT_PUBLIC_DINOLEND_CHAIN_ID || '31337'
) as ChainId;
const getDinolendMarketTitle = () => {
  if (dinolendMarketChainId === ChainId.sepolia) return 'Dinolend Sepolia';
  if ((dinolendMarketChainId as number) === 31337) return 'Dinolend Devnet';
  return `Dinolend ${dinolendMarketChainId}`;
};
const dinolendMarketTitle = getDinolendMarketTitle();
const legacyDinolendMarketAliases = new Set(['dinolend_v3', ['dinolend', 'local', 'v3'].join('_')]);

const requiredDinolendMarketEnvNames = [
  'NEXT_PUBLIC_DINOLEND_POOL_ADDRESSES_PROVIDER',
  'NEXT_PUBLIC_DINOLEND_POOL',
  'NEXT_PUBLIC_DINOLEND_WETH_GATEWAY',
  'NEXT_PUBLIC_DINOLEND_WALLET_BALANCE_PROVIDER',
  'NEXT_PUBLIC_DINOLEND_UI_POOL_DATA_PROVIDER',
] as const;

export const isDinolendMarketEnvConfigured = (
  env: Record<string, string | undefined> = process.env
) =>
  requiredDinolendMarketEnvNames.every((name) =>
    addressPattern.test((env[name] || dinolendEnv[name]) ?? '')
  );

export const isDinolendMarketConfigured = isDinolendMarketEnvConfigured();
export const isDinolendMarketAvailable = isDinolendMarketConfigured;

// Enable for premissioned market
// import { PermissionView } from 'src/components/transactions/FlowCommons/PermissionView';
export type MarketDataType = {
  v3?: boolean;
  marketTitle: string;
  market: CustomMarket;
  // the network the market operates on
  chainId: ChainId;
  enabledFeatures?: {
    liquiditySwap?: boolean;
    staking?: boolean;
    governance?: boolean;
    faucet?: boolean;
    collateralRepay?: boolean;
    incentives?: boolean;
    permissions?: boolean;
    debtSwitch?: boolean;
    withdrawAndSwitch?: boolean;
    switch?: boolean;
    limit?: boolean;
  };
  permitDisabled?: boolean; // intended to be used for testnets
  isFork?: boolean;
  permissionComponent?: ReactNode;
  subgraphUrl?: string;
  logo?: string;
  externalUrl?: string; // URL for external markets like Aptos
  addresses: {
    LENDING_POOL_ADDRESS_PROVIDER: string;
    LENDING_POOL: string;
    WETH_GATEWAY?: string;
    SWAP_COLLATERAL_ADAPTER?: string;
    REPAY_WITH_COLLATERAL_ADAPTER?: string;
    DEBT_SWITCH_ADAPTER?: string;
    WITHDRAW_SWITCH_ADAPTER?: string;
    FAUCET?: string;
    PERMISSION_MANAGER?: string;
    WALLET_BALANCE_PROVIDER: string;
    L2_ENCODER?: string;
    UI_POOL_DATA_PROVIDER: string;
    UI_INCENTIVE_DATA_PROVIDER?: string;
    COLLECTOR?: string;
    V3_MIGRATOR?: string;
    GHO_TOKEN_ADDRESS?: string;
  };
};
export enum CustomMarket {
  dinolend_configured_v3 = 'dinolend_configured_v3',
  // v3 test networks, all v3.0.1
  proto_base_sepolia_v3 = 'proto_base_sepolia_v3',
  // v3 mainnets
  proto_mainnet_v3 = 'proto_mainnet_v3',
  proto_optimism_v3 = 'proto_optimism_v3',
  proto_avalanche_v3 = 'proto_avalanche_v3',
  proto_polygon_v3 = 'proto_polygon_v3',
  proto_arbitrum_v3 = 'proto_arbitrum_v3',
  proto_metis_v3 = 'proto_metis_v3',
  proto_base_v3 = 'proto_base_v3',
  proto_gnosis_v3 = 'proto_gnosis_v3',
  proto_bnb_v3 = 'proto_bnb_v3',
  proto_scroll_v3 = 'proto_scroll_v3',
  proto_lido_v3 = 'proto_lido_v3',
  proto_zksync_v3 = 'proto_zksync_v3',
  proto_etherfi_v3 = 'proto_etherfi_v3',
  proto_linea_v3 = 'proto_linea_v3',
  proto_sonic_v3 = 'proto_sonic_v3',
  proto_celo_v3 = 'proto_celo_v3',
  proto_soneium_v3 = 'proto_soneium_v3',
  proto_horizon_v3 = 'proto_horizon_v3',
  proto_aptos_v3 = 'proto_aptos_v3',
  proto_plasma_v3 = 'proto_plasma_v3',
  proto_ink_v3 = 'proto_ink_v3',
  proto_megaeth_v3 = 'proto_megaeth_v3',
  proto_mantle_v3 = 'proto_mantle_v3',
  proto_xlayer_v3 = 'proto_xlayer_v3',
  // v2
  proto_mainnet = 'proto_mainnet',
  proto_avalanche = 'proto_avalanche',
  proto_fuji = 'proto_fuji',
  proto_polygon = 'proto_polygon',

  // external
  // permissioned_market = 'permissioned_market',
}

export const normalizeCustomMarket = (market?: string | null): CustomMarket | undefined => {
  if (!market) {
    return undefined;
  }
  if (legacyDinolendMarketAliases.has(market)) {
    return CustomMarket.dinolend_configured_v3;
  }
  if (Object.values(CustomMarket).includes(market as CustomMarket)) {
    return market as CustomMarket;
  }
  return undefined;
};

export const marketsData: {
  [key in keyof typeof CustomMarket]: MarketDataType;
} = {
  [CustomMarket.dinolend_configured_v3]: {
    marketTitle: dinolendMarketTitle,
    market: CustomMarket.dinolend_configured_v3,
    chainId: dinolendMarketChainId,
    v3: true,
    permitDisabled: true,
    logo: '/dinolend-logo.svg',
    enabledFeatures: {
      faucet: false,
      governance: false,
      staking: false,
      liquiditySwap: false,
      collateralRepay: false,
      incentives: false,
      withdrawAndSwitch: false,
      debtSwitch: false,
      switch: false,
      limit: false,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: readEnvAddress('NEXT_PUBLIC_DINOLEND_POOL_ADDRESSES_PROVIDER'),
      LENDING_POOL: readEnvAddress('NEXT_PUBLIC_DINOLEND_POOL'),
      WETH_GATEWAY: readEnvAddress('NEXT_PUBLIC_DINOLEND_WETH_GATEWAY'),
      WALLET_BALANCE_PROVIDER: readEnvAddress('NEXT_PUBLIC_DINOLEND_WALLET_BALANCE_PROVIDER'),
      UI_POOL_DATA_PROVIDER: readEnvAddress('NEXT_PUBLIC_DINOLEND_UI_POOL_DATA_PROVIDER'),
      UI_INCENTIVE_DATA_PROVIDER: readEnvAddress('NEXT_PUBLIC_DINOLEND_UI_INCENTIVE_DATA_PROVIDER'),
      COLLECTOR: readEnvAddress('NEXT_PUBLIC_DINOLEND_COLLECTOR'),
    },
  },
  [CustomMarket.proto_mainnet_v3]: {
    marketTitle: 'Core',
    market: CustomMarket.proto_mainnet_v3,
    chainId: ChainId.mainnet,
    v3: true,
    enabledFeatures: {
      governance: true,
      staking: true,
      liquiditySwap: true,
      collateralRepay: true,
      incentives: true,
      withdrawAndSwitch: true,
      debtSwitch: true,
      switch: true,
      limit: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Ethereum.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Ethereum.POOL,
      WETH_GATEWAY: V3Ethereum.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: V3Ethereum.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: V3Ethereum.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V3Ethereum.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2dad8162a989cd99d673de4425bb2298db1e1aa2', // V3Ethereum.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Ethereum.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Ethereum.COLLECTOR,
      GHO_TOKEN_ADDRESS: V3Ethereum.ASSETS.GHO.UNDERLYING,
      WITHDRAW_SWITCH_ADAPTER: V3Ethereum.WITHDRAW_SWAP_ADAPTER,
      DEBT_SWITCH_ADAPTER: V3Ethereum.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_lido_v3]: {
    marketTitle: 'Prime',
    market: CustomMarket.proto_lido_v3,
    chainId: ChainId.mainnet,
    v3: true,
    logo: '/icons/markets/lido.svg',
    enabledFeatures: {
      governance: true,
      staking: true,
      liquiditySwap: true,
      collateralRepay: true,
      incentives: true,
      withdrawAndSwitch: true,
      debtSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3EthereumLido.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3EthereumLido.POOL,
      WETH_GATEWAY: V3EthereumLido.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: V3EthereumLido.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: V3EthereumLido.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V3EthereumLido.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2dad8162a989cd99d673de4425bb2298db1e1aa2', //V3EthereumLido.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3EthereumLido.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Ethereum.COLLECTOR,
      WITHDRAW_SWITCH_ADAPTER: V3EthereumLido.WITHDRAW_SWAP_ADAPTER,
      DEBT_SWITCH_ADAPTER: V3EthereumLido.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_mainnet]: {
    marketTitle: 'Ethereum',
    market: CustomMarket.proto_mainnet,
    chainId: ChainId.mainnet,
    enabledFeatures: {
      governance: true,
      staking: true,
      liquiditySwap: true,
      collateralRepay: false,
      incentives: true,
      debtSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V2Ethereum.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V2Ethereum.POOL,
      WETH_GATEWAY: V2Ethereum.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: V2Ethereum.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: V2Ethereum.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V2Ethereum.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: V2Ethereum.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V2Ethereum.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V2Ethereum.COLLECTOR,
      V3_MIGRATOR: V2Ethereum.MIGRATION_HELPER,
      DEBT_SWITCH_ADAPTER: V2Ethereum.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_polygon]: {
    marketTitle: 'Polygon',
    market: CustomMarket.proto_polygon,
    chainId: ChainId.polygon,
    enabledFeatures: {
      liquiditySwap: true,
      incentives: true,
      collateralRepay: false,
      debtSwitch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V2Polygon.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V2Polygon.POOL,
      WETH_GATEWAY: V2Polygon.WETH_GATEWAY,
      SWAP_COLLATERAL_ADAPTER: V2Polygon.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: V2Polygon.REPAY_WITH_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V2Polygon.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: V2Polygon.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V2Polygon.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V2Polygon.COLLECTOR,
      V3_MIGRATOR: V2Polygon.MIGRATION_HELPER,
      DEBT_SWITCH_ADAPTER: V2Polygon.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_avalanche]: {
    marketTitle: 'Avalanche',
    market: CustomMarket.proto_avalanche,
    chainId: ChainId.avalanche,
    enabledFeatures: {
      liquiditySwap: true,
      incentives: true,
      collateralRepay: false,
      debtSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V2Avalanche.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V2Avalanche.POOL,
      WETH_GATEWAY: V2Avalanche.WETH_GATEWAY,
      SWAP_COLLATERAL_ADAPTER: V2Avalanche.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: V2Avalanche.REPAY_WITH_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V2Avalanche.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: V2Avalanche.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V2Avalanche.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V2Avalanche.COLLECTOR,
      V3_MIGRATOR: V2Avalanche.MIGRATION_HELPER,
      DEBT_SWITCH_ADAPTER: V2Avalanche.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_base_v3]: {
    marketTitle: 'Base',
    market: CustomMarket.proto_base_v3,
    v3: true,
    chainId: ChainId.base,
    enabledFeatures: {
      incentives: true,
      liquiditySwap: true,
      withdrawAndSwitch: true,
      collateralRepay: true,
      debtSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Base.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Base.POOL,
      WETH_GATEWAY: V3Base.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3Base.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x0c6bc4a12039788be08f87e87cff87fedbd1d386', // V3Base.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Base.UI_INCENTIVE_DATA_PROVIDER,
      L2_ENCODER: V3Base.L2_ENCODER,
      COLLECTOR: V3Base.COLLECTOR,
      REPAY_WITH_COLLATERAL_ADAPTER: V3Base.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: V3Base.SWAP_COLLATERAL_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: V3Base.WITHDRAW_SWAP_ADAPTER,
      DEBT_SWITCH_ADAPTER: V3Base.DEBT_SWAP_ADAPTER,
      GHO_TOKEN_ADDRESS: V3Base.ASSETS.GHO.UNDERLYING,
    },
  },

  [CustomMarket.proto_arbitrum_v3]: {
    marketTitle: 'Arbitrum',
    market: CustomMarket.proto_arbitrum_v3,
    v3: true,
    chainId: ChainId.arbitrum_one,
    enabledFeatures: {
      incentives: true,
      liquiditySwap: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
      limit: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Arbitrum.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Arbitrum.POOL,
      WETH_GATEWAY: V3Arbitrum.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3Arbitrum.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x91e04cf78e53aebe609e8a7f2003e7eecd743f2b', // V3Arbitrum.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Arbitrum.UI_INCENTIVE_DATA_PROVIDER,
      L2_ENCODER: V3Arbitrum.L2_ENCODER,
      COLLECTOR: V3Arbitrum.COLLECTOR,
      SWAP_COLLATERAL_ADAPTER: V3Arbitrum.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: V3Arbitrum.REPAY_WITH_COLLATERAL_ADAPTER,
      DEBT_SWITCH_ADAPTER: V3Arbitrum.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: V3Arbitrum.WITHDRAW_SWAP_ADAPTER,
      GHO_TOKEN_ADDRESS: V3Arbitrum.ASSETS.GHO.UNDERLYING,
    },
  },
  [CustomMarket.proto_base_sepolia_v3]: {
    marketTitle: 'Base Sepolia',
    market: CustomMarket.proto_base_sepolia_v3,
    v3: true,
    permitDisabled: true,
    chainId: ChainId.base_sepolia,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3BaseSepolia.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3BaseSepolia.POOL,
      WETH_GATEWAY: V3BaseSepolia.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3BaseSepolia.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x3cB7B00B6C09B71998124196691e8bF2694De863', // V3BaseSepolia.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3BaseSepolia.UI_INCENTIVE_DATA_PROVIDER,
      L2_ENCODER: V3BaseSepolia.L2_ENCODER,
      FAUCET: '0xD9145b5F45Ad4519c7ACcD6E0A4A82e83bB8A6Dc',
    },
  },
  [CustomMarket.proto_avalanche_v3]: {
    marketTitle: 'Avalanche',
    market: CustomMarket.proto_avalanche_v3,
    v3: true,
    chainId: ChainId.avalanche,
    enabledFeatures: {
      liquiditySwap: true,
      incentives: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Avalanche.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Avalanche.POOL,
      WETH_GATEWAY: V3Avalanche.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: V3Avalanche.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: V3Avalanche.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V3Avalanche.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xFBa4Df643205c5400BC3e05a1E67E0dFaEeeb41F', // V3Avalanche.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Avalanche.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Avalanche.COLLECTOR,
      DEBT_SWITCH_ADAPTER: V3Avalanche.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: V3Avalanche.WITHDRAW_SWAP_ADAPTER,
      GHO_TOKEN_ADDRESS: GhoAvalanche.GHO_TOKEN,
    },
  },
  [CustomMarket.proto_linea_v3]: {
    marketTitle: 'Linea',
    market: CustomMarket.proto_linea_v3,
    chainId: ChainId.linea,
    logo: '/icons/markets/linea.svg',
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Linea.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Linea.POOL,
      WETH_GATEWAY: V3Linea.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3Linea.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dce6a469cc33be3121b6b2d4cad2763', // V3Linea.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Linea.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Linea.COLLECTOR,
    },
    enabledFeatures: {
      liquiditySwap: true,
      withdrawAndSwitch: true,
      collateralRepay: true,
      debtSwitch: true,
      switch: true,
    },
  },
  [CustomMarket.proto_sonic_v3]: {
    marketTitle: 'Sonic',
    market: CustomMarket.proto_sonic_v3,
    chainId: ChainId.sonic,
    v3: true,
    enabledFeatures: {
      collateralRepay: true,
      liquiditySwap: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Sonic.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Sonic.POOL,
      WETH_GATEWAY: V3Sonic.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3Sonic.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xe28e2c8d240dd5ebd0adcab86fbd79df7a052034', // V3Sonic.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Sonic.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Sonic.COLLECTOR,
      SWAP_COLLATERAL_ADAPTER: V3Sonic.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: V3Sonic.REPAY_WITH_COLLATERAL_ADAPTER,
      DEBT_SWITCH_ADAPTER: V3Sonic.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: V3Sonic.WITHDRAW_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_optimism_v3]: {
    marketTitle: 'OP',
    market: CustomMarket.proto_optimism_v3,
    v3: true,
    chainId: ChainId.optimism,
    enabledFeatures: {
      incentives: true,
      collateralRepay: true,
      liquiditySwap: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Optimism.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Optimism.POOL,
      WETH_GATEWAY: V3Optimism.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3Optimism.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x68100bd5345ea474d93577127c11f39ff8463e93', // V3Optimism.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Optimism.UI_INCENTIVE_DATA_PROVIDER,
      L2_ENCODER: V3Optimism.L2_ENCODER,
      COLLECTOR: V3Optimism.COLLECTOR,
      SWAP_COLLATERAL_ADAPTER: V3Optimism.SWAP_COLLATERAL_ADAPTER,
      REPAY_WITH_COLLATERAL_ADAPTER: V3Optimism.REPAY_WITH_COLLATERAL_ADAPTER,
      DEBT_SWITCH_ADAPTER: V3Optimism.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: V3Optimism.WITHDRAW_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_horizon_v3]: {
    marketTitle: 'Dinolend Horizon',
    market: CustomMarket.proto_horizon_v3,
    chainId: ChainId.mainnet,
    v3: true,
    logo: '/icons/markets/horizon.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: '0x5D39E06b825C1F2B80bf2756a73e28eFAA128ba0',
      LENDING_POOL: '0xAe05Cd22df81871bc7cC2a04BeCfb516bFe332C8',
      WETH_GATEWAY: '0x973195fB8F67F5B0afe7beDB2A02cec829d89991',
      // REPAY_WITH_COLLATERAL_ADAPTER: V3EthereumLido.REPAY_WITH_COLLATERAL_ADAPTER,
      // SWAP_COLLATERAL_ADAPTER: V3EthereumLido.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: '0xd8F7829ceB2692C90e418e0963b5Cbcbafc260Bd',
      UI_POOL_DATA_PROVIDER: '0x2dad8162a989cd99d673de4425bb2298db1e1aa2', // V3Ethereum.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: '0xC5E1717BEafC680E0148DD561591410b69650a5F',
      COLLECTOR: V3EthereumHorizon.COLLECTOR,
      // WITHDRAW_SWITCH_ADAPTER: V3EthereumLido.WITHDRAW_SWAP_ADAPTER,
      // DEBT_SWITCH_ADAPTER: V3EthereumLido.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_plasma_v3]: {
    marketTitle: 'Plasma',
    market: CustomMarket.proto_plasma_v3,
    chainId: ChainId.plasma,
    v3: true,
    logo: '/icons/networks/plasma.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Plasma.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Plasma.POOL,
      WETH_GATEWAY: V3Plasma.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3Plasma.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2E549104c516b8657A7D888494DfbAbD7C70b464', // V3Plasma.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Plasma.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Plasma.COLLECTOR,
      GHO_TOKEN_ADDRESS: GhoPlasma.GHO_TOKEN,
    },
    enabledFeatures: {
      // liquiditySwap: true,
      // withdrawAndSwitch: true,
      // collateralRepay: true,
      // debtSwitch: true,
      // switch: true,
    },
  },
  [CustomMarket.proto_polygon_v3]: {
    marketTitle: 'Polygon',
    market: CustomMarket.proto_polygon_v3,
    chainId: ChainId.polygon,
    v3: true,
    enabledFeatures: {
      liquiditySwap: true,
      incentives: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Polygon.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Polygon.POOL,
      WETH_GATEWAY: V3Polygon.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: V3Polygon.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: V3Polygon.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V3Polygon.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x66E1aBdb06e7363a618D65a910c540dfED23754f', // V3Polygon.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Polygon.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Polygon.COLLECTOR,
      DEBT_SWITCH_ADAPTER: V3Polygon.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: V3Polygon.WITHDRAW_SWAP_ADAPTER,
    },
  },

  [CustomMarket.proto_ink_v3]: {
    marketTitle: 'Ink',
    market: CustomMarket.proto_ink_v3,
    chainId: ChainId.ink,
    v3: true,
    logo: '/icons/networks/ink.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3InkWhitelabel.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3InkWhitelabel.POOL,
      WETH_GATEWAY: V3InkWhitelabel.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3InkWhitelabel.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2E549104c516b8657A7D888494DfbAbD7C70b464', // V3InkWhitelabel.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3InkWhitelabel.UI_INCENTIVE_DATA_PROVIDER,
      GHO_TOKEN_ADDRESS: V3InkWhitelabel.ASSETS.GHO.UNDERLYING,
      COLLECTOR: V3InkWhitelabel.COLLECTOR,
    },
  },

  [CustomMarket.proto_megaeth_v3]: {
    marketTitle: 'MegaETH',
    market: CustomMarket.proto_megaeth_v3,
    chainId: 4326 as ChainId,
    v3: true,
    logo: '/icons/networks/megaeth.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3MegaEth.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3MegaEth.POOL,
      WETH_GATEWAY: V3MegaEth.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3MegaEth.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xE28E2c8d240dd5eBd0adcab86fbD79df7a052034', // V3MegaEth.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3MegaEth.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3MegaEth.COLLECTOR,
      L2_ENCODER: V3MegaEth.L2_ENCODER,
    },
  },
  [CustomMarket.proto_xlayer_v3]: {
    marketTitle: 'X Layer',
    market: CustomMarket.proto_xlayer_v3,
    chainId: ChainId.xlayer,
    logo: '/icons/networks/xlayer.svg',

    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3XLayer.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3XLayer.POOL,
      WETH_GATEWAY: V3XLayer.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3XLayer.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dce6a469cc33be3121b6b2d4cad2763', // V3XLayer.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3XLayer.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3XLayer.COLLECTOR,
    },
  },
  [CustomMarket.proto_mantle_v3]: {
    marketTitle: 'Mantle',
    market: CustomMarket.proto_mantle_v3,
    chainId: ChainId.mantle,
    v3: true,
    logo: '/icons/networks/mantle.svg',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Mantle.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Mantle.POOL,
      WETH_GATEWAY: V3Mantle.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3Mantle.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dce6a469cc33be3121b6b2d4cad2763', // V3Mantle.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Mantle.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Mantle.COLLECTOR,
      L2_ENCODER: V3Mantle.L2_ENCODER,
      GHO_TOKEN_ADDRESS: GhoMantle.GHO_TOKEN,
    },
  },
  [CustomMarket.proto_fuji]: {
    marketTitle: 'Avalanche Fuji',
    market: CustomMarket.proto_fuji,
    chainId: ChainId.fuji,
    enabledFeatures: {
      faucet: true,
      incentives: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V2Fuji.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V2Fuji.POOL,
      WETH_GATEWAY: V2Fuji.WETH_GATEWAY,
      FAUCET: V2Fuji.FAUCET,
      WALLET_BALANCE_PROVIDER: V2Fuji.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: V2Fuji.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V2Fuji.UI_INCENTIVE_DATA_PROVIDER,
    },
  },

  [CustomMarket.proto_gnosis_v3]: {
    marketTitle: 'Gnosis',
    market: CustomMarket.proto_gnosis_v3,
    chainId: ChainId.xdai,
    v3: true,
    enabledFeatures: {
      liquiditySwap: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Gnosis.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Gnosis.POOL,
      WETH_GATEWAY: V3Gnosis.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: V3Gnosis.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: V3Gnosis.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V3Gnosis.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x0C6BC4a12039788be08F87e87Cff87FEDbd1D386', // V3Gnosis.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Gnosis.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Gnosis.COLLECTOR,
      DEBT_SWITCH_ADAPTER: V3Gnosis.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: V3Gnosis.WITHDRAW_SWAP_ADAPTER,
      GHO_TOKEN_ADDRESS: GhoGnosis.GHO_TOKEN,
    },
  },
  [CustomMarket.proto_bnb_v3]: {
    marketTitle: 'BNB Chain',
    market: CustomMarket.proto_bnb_v3,
    chainId: ChainId.bnb,
    v3: true,
    enabledFeatures: {
      liquiditySwap: true,
      collateralRepay: true,
      debtSwitch: true,
      withdrawAndSwitch: true,
      switch: true,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3BNB.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3BNB.POOL,
      WETH_GATEWAY: V3BNB.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: V3BNB.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: V3BNB.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V3BNB.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x68100bD5345eA474D93577127C11F39FF8463e93', // V3BNB.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3BNB.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3BNB.COLLECTOR,
      DEBT_SWITCH_ADAPTER: V3BNB.DEBT_SWAP_ADAPTER,
      WITHDRAW_SWITCH_ADAPTER: V3BNB.WITHDRAW_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_scroll_v3]: {
    marketTitle: 'Scroll',
    market: CustomMarket.proto_scroll_v3,
    chainId: ChainId.scroll,
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Scroll.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Scroll.POOL,
      WETH_GATEWAY: V3Scroll.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3Scroll.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xe28e2c8d240dd5ebd0adcab86fbd79df7a052034', // V3Scroll.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Scroll.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Scroll.COLLECTOR,
    },
  },
  [CustomMarket.proto_zksync_v3]: {
    marketTitle: 'ZKsync',
    market: CustomMarket.proto_zksync_v3,
    chainId: ChainId.zksync,
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3ZkSync.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3ZkSync.POOL,
      WETH_GATEWAY: V3ZkSync.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3ZkSync.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x756Ff6722543F12d25396Ea646B0F2C96dA70c3e', // V3ZkSync.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3ZkSync.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3ZkSync.COLLECTOR,
    },
  },
  [CustomMarket.proto_aptos_v3]: {
    marketTitle: 'Aptos',
    market: CustomMarket.proto_aptos_v3,
    v3: true,
    chainId: ChainId.mainnet, // Using mainnet since Aptos is external
    logo: '/icons/markets/aptos.svg',
    externalUrl: '/',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: '',
      LENDING_POOL: '',
      WALLET_BALANCE_PROVIDER: '',
      UI_POOL_DATA_PROVIDER: '',
      UI_INCENTIVE_DATA_PROVIDER: '',
    },
  },

  [CustomMarket.proto_celo_v3]: {
    marketTitle: 'Celo',
    market: CustomMarket.proto_celo_v3,
    chainId: ChainId.celo,
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Celo.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Celo.POOL,
      WALLET_BALANCE_PROVIDER: V3Celo.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dcE6A469CC33BE3121b6B2D4CaD2763', // V3Celo.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Celo.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Celo.COLLECTOR,
    },
  },
  [CustomMarket.proto_soneium_v3]: {
    marketTitle: 'Soneium',
    market: CustomMarket.proto_soneium_v3,
    chainId: ChainId.soneium,
    v3: true,
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Soneium.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Soneium.POOL,
      WETH_GATEWAY: V3Soneium.WETH_GATEWAY,
      WALLET_BALANCE_PROVIDER: V3Soneium.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0xc851e6147dce6a469cc33be3121b6b2d4cad2763', // V3Soneium.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Soneium.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Soneium.COLLECTOR,
    },
  },
  [CustomMarket.proto_etherfi_v3]: {
    marketTitle: 'EtherFi',
    market: CustomMarket.proto_etherfi_v3,
    chainId: ChainId.mainnet,
    v3: true,
    logo: '/icons/markets/etherfi.svg',
    enabledFeatures: {
      governance: true,
      staking: true,
      liquiditySwap: true,
      collateralRepay: true,
      incentives: true,
      withdrawAndSwitch: true,
      debtSwitch: true,
      switch: false,
    },
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3EthereumEtherFi.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3EthereumEtherFi.POOL,
      WETH_GATEWAY: V3EthereumEtherFi.WETH_GATEWAY,
      REPAY_WITH_COLLATERAL_ADAPTER: V3EthereumEtherFi.REPAY_WITH_COLLATERAL_ADAPTER,
      SWAP_COLLATERAL_ADAPTER: V3EthereumEtherFi.SWAP_COLLATERAL_ADAPTER,
      WALLET_BALANCE_PROVIDER: V3EthereumEtherFi.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x2dad8162a989cd99d673de4425bb2298db1e1aa2', // V3EthereumEtherFi.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3EthereumEtherFi.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3EthereumEtherFi.COLLECTOR,
      WITHDRAW_SWITCH_ADAPTER: V3EthereumEtherFi.WITHDRAW_SWAP_ADAPTER,
      DEBT_SWITCH_ADAPTER: V3EthereumEtherFi.DEBT_SWAP_ADAPTER,
    },
  },
  [CustomMarket.proto_metis_v3]: {
    marketTitle: 'Metis',
    market: CustomMarket.proto_metis_v3,
    chainId: ChainId.metis_andromeda,
    v3: true,
    enabledFeatures: {
      incentives: true,
    },
    subgraphUrl: '',
    addresses: {
      LENDING_POOL_ADDRESS_PROVIDER: V3Metis.POOL_ADDRESSES_PROVIDER,
      LENDING_POOL: V3Metis.POOL,
      WETH_GATEWAY: '0x0', // not applicable for Metis
      WALLET_BALANCE_PROVIDER: V3Metis.WALLET_BALANCE_PROVIDER,
      UI_POOL_DATA_PROVIDER: '0x5c5228ac8bc1528482514af3e27e692495148717', // V3Metis.UI_POOL_DATA_PROVIDER,
      UI_INCENTIVE_DATA_PROVIDER: V3Metis.UI_INCENTIVE_DATA_PROVIDER,
      COLLECTOR: V3Metis.COLLECTOR,
    },
  },
} as const;

export const findByChainId = (chainId: ChainId) => {
  const candidates = Object.values(marketsData).filter((market) => market.chainId === chainId);
  if (isDinolendMarketAvailable) {
    return (
      candidates.find((market) => market.market === CustomMarket.dinolend_configured_v3) ||
      candidates[0]
    );
  }
  return candidates[0];
};
